import "react-toastify/dist/ReactToastify.css";
import "react-toastify/dist/ReactToastify.css";
import { calculateDistance } from "../../utils/calculateDistance";
import { getVehicleDirection, updateVehicleDirection } from "./dashboardAPI";

// ====== CONFIG CONSTANTS ====== 
const MOVEMENT_THRESHOLD = 11; // meters movement to count as "moved"
const TERMINAL_THRESHOLD = 75; // meters to consider "at endpoint"
const ROUTE_PROXIMITY_THRESHOLD = 100; // must be this close to route to process direction
const MIN_POINTS_FOR_DIRECTION = 6; // min history points before making a decision
const BACKUP_POINTS_FOR_RECHECK = 20; // total retained history for rechecking
const RECHECK_EVERY_UPDATES = 8; // perform backup direction recheck every N updates
const API_UPDATE_DEBOUNCE = 10000; ///check every 30 seconds
// ====== INTERNAL MEMORY ======
const prevDirectionsRef = {};
const recheckCounterRef = {};
const pendingApiUpdates = new Map(); // Track pending API updates to prevent race conditions
const apiUpdateTimers = new Map(); // Debounce timers for API updates


// ====== HELPERS ======
export const deviceIdsEqual = (left, right) =>
  left != null && right != null && String(left) === String(right);

export const findByDeviceId = (items = [], deviceId) =>
  items.find(
    (item) =>
      deviceIdsEqual(item?.deviceId, deviceId) ||
      deviceIdsEqual(item?.device_id, deviceId),
  );

export const normalizeDeviceData = (data) => {
  const latitude = data.latitude || data.Latitude;
  const longitude = data.longitude || data.Longitude;
  const deviceId = data.deviceId || data.device_id;
  if (deviceId && latitude && longitude) {
    return { ...data, deviceId, latitude, longitude };
  }
  return {
    deviceId,
    latitude,
    longitude,
    timestamp: new Date().toISOString(),
    rawData: data.RawData || "",
  };
};

// Normalizes a prediction frame coming through the same SSE pipe as live data.
// Backend tags these with `type: "prediction"` and injects `device_id`; payload
// uses `lat`/`lon` (numbers) and `timestamp` in unix seconds.
// Returns null for anything that isn't a valid prediction so callers can
// branch safely without try/catch.
export const normalizePredictionData = (data) => {
  if (!data || data.type !== "prediction") return null;

  const deviceId = data.device_id || data.deviceId;
  const lat = parseFloat(data.lat ?? data.latitude ?? data.Latitude);
  const lng = parseFloat(data.lon ?? data.lng ?? data.longitude ?? data.Longitude);
  if (!deviceId || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return {
    deviceId,
    type: "prediction",
    latitude: lat,
    longitude: lng,
    lat,
    lng,
    confidence: typeof data.confidence === "number" ? data.confidence : 1,
    speedKmph: typeof data.speed_kmph === "number" ? data.speed_kmph : null,
    // backend timestamp is unix seconds (float) → normalize to ms
    predictedAt: data.timestamp ? data.timestamp * 1000 : Date.now(),
  };
};

// Replace your serializeDirections function with this:
export const serializeDirections = (directionsResult) => {
  try {
    // Add safety checks for undefined/null values
    if (!directionsResult || !directionsResult.routes || !Array.isArray(directionsResult.routes)) {
      console.warn('Invalid directionsResult structure:', directionsResult);
      return null;
    }

    return {
      // Essential top-level properties
      status: directionsResult.status,
      request: directionsResult.request ? {
        origin: directionsResult.request.origin ? {
          lat: typeof directionsResult.request.origin.lat === 'function'
            ? directionsResult.request.origin.lat()
            : directionsResult.request.origin.lat,
          lng: typeof directionsResult.request.origin.lng === 'function'
            ? directionsResult.request.origin.lng()
            : directionsResult.request.origin.lng
        } : null,
        destination: directionsResult.request.destination ? {
          lat: typeof directionsResult.request.destination.lat === 'function'
            ? directionsResult.request.destination.lat()
            : directionsResult.request.destination.lat,
          lng: typeof directionsResult.request.destination.lng === 'function'
            ? directionsResult.request.destination.lng()
            : directionsResult.request.destination.lng
        } : null,
        travelMode: directionsResult.request.travelMode,
        waypoints: directionsResult.request.waypoints || [],
        optimizeWaypoints: directionsResult.request.optimizeWaypoints || false
      } : null,
      routes: directionsResult.routes.map(route => ({
        // Essential route properties
        bounds: route.bounds ? {
          north: typeof route.bounds.getNorthEast === 'function'
            ? route.bounds.getNorthEast().lat()
            : route.bounds.north,
          south: typeof route.bounds.getSouthWest === 'function'
            ? route.bounds.getSouthWest().lat()
            : route.bounds.south,
          east: typeof route.bounds.getNorthEast === 'function'
            ? route.bounds.getNorthEast().lng()
            : route.bounds.east,
          west: typeof route.bounds.getSouthWest === 'function'
            ? route.bounds.getSouthWest().lng()
            : route.bounds.west
        } : null,
        copyrights: route.copyrights || '',
        fare: route.fare || null,
        warnings: route.warnings || [],
        waypoint_order: route.waypoint_order || [],

        // Detailed legs data - add safety check for legs
        legs: (route.legs || []).map(leg => ({
          distance: leg.distance || null,
          duration: leg.duration || null,
          duration_in_traffic: leg.duration_in_traffic || null,
          end_address: leg.end_address || '',
          end_location: leg.end_location ? {
            lat: typeof leg.end_location.lat === 'function' ? leg.end_location.lat() : leg.end_location.lat,
            lng: typeof leg.end_location.lng === 'function' ? leg.end_location.lng() : leg.end_location.lng
          } : null,
          start_address: leg.start_address || '',
          start_location: leg.start_location ? {
            lat: typeof leg.start_location.lat === 'function' ? leg.start_location.lat() : leg.start_location.lat,
            lng: typeof leg.start_location.lng === 'function' ? leg.start_location.lng() : leg.start_location.lng
          } : null,
          steps: (leg.steps || []).map(step => ({
            distance: step.distance || null,
            duration: step.duration || null,
            end_location: step.end_location ? {
              lat: typeof step.end_location.lat === 'function' ? step.end_location.lat() : step.end_location.lat,
              lng: typeof step.end_location.lng === 'function' ? step.end_location.lng() : step.end_location.lng
            } : null,
            instructions: step.instructions || '',
            path: (step.path || []).map(point => ({
              lat: typeof point.lat === 'function' ? point.lat() : point.lat,
              lng: typeof point.lng === 'function' ? point.lng() : point.lng
            })),
            start_location: step.start_location ? {
              lat: typeof step.start_location.lat === 'function' ? step.start_location.lat() : step.start_location.lat,
              lng: typeof step.start_location.lng === 'function' ? step.start_location.lng() : step.start_location.lng
            } : null,
            travel_mode: step.travel_mode || null
          }))
        })),

        // Overview path for polyline rendering
        overview_path: (route.overview_path || []).map(point => ({
          lat: typeof point.lat === 'function' ? point.lat() : point.lat,
          lng: typeof point.lng === 'function' ? point.lng() : point.lng
        })),

        // Overview polyline (encoded)
        overview_polyline: route.overview_polyline || null
      }))
    };
  } catch (error) {
    console.error('Error serializing directions:', error);
    return null;
  }
};
// Helper to create a unique cache key incorporating route and stops state (to avoid stale cache)
export const createCacheKey = (route, stops) => {
  return (
    `directions_${route.id}_` +
    stops
      .map(
        (s) =>
          `${s.name}_${parseFloat(s.latitude).toFixed(6)}_${parseFloat(
            s.longitude
          ).toFixed(6)}`
      )
      .join("-")
  );
};

export const getOrderedRouteCoordinates = (route) =>
  [...(route?.routeCoordinates || [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter((coord) => {
      const lat = parseFloat(coord.latitude);
      const lng = parseFloat(coord.longitude);
      return !Number.isNaN(lat) && !Number.isNaN(lng);
    });

export const isValidDirectionsResult = (result) =>
  Boolean(
    result?.routes?.[0]?.legs?.length > 0,
  );

export const getRouteLabel = (route) =>
  route?.name || route?.routeId || `route ${route?.id}`;

// Rehydrate directions data - recreate complete Google Maps DirectionsResult
export const rehydrateDirections = (serializedData) => {
  try {
    return {
      status: serializedData.status || window.google.maps.DirectionsStatus.OK,
      request: serializedData.request ? {
        origin: serializedData.request.origin
          ? new window.google.maps.LatLng(serializedData.request.origin.lat, serializedData.request.origin.lng)
          : null,
        destination: serializedData.request.destination
          ? new window.google.maps.LatLng(serializedData.request.destination.lat, serializedData.request.destination.lng)
          : null,
        travelMode: serializedData.request.travelMode || window.google.maps.TravelMode.DRIVING,
        waypoints: serializedData.request.waypoints || [],
        optimizeWaypoints: serializedData.request.optimizeWaypoints || false
      } : null,
      routes: serializedData.routes.map(route => ({
        bounds: route.bounds ? new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(route.bounds.south, route.bounds.west),
          new window.google.maps.LatLng(route.bounds.north, route.bounds.east)
        ) : null,
        copyrights: route.copyrights || '',
        fare: route.fare || null,
        warnings: route.warnings || [],
        waypoint_order: route.waypoint_order || [],

        legs: route.legs.map(leg => ({
          distance: leg.distance || null,
          duration: leg.duration || null,
          duration_in_traffic: leg.duration_in_traffic || null,
          end_address: leg.end_address || '',
          end_location: leg.end_location
            ? new window.google.maps.LatLng(leg.end_location.lat, leg.end_location.lng)
            : null,
          start_address: leg.start_address || '',
          start_location: leg.start_location
            ? new window.google.maps.LatLng(leg.start_location.lat, leg.start_location.lng)
            : null,
          steps: leg.steps.map(step => ({
            distance: step.distance || null,
            duration: step.duration || null,
            end_location: step.end_location
              ? new window.google.maps.LatLng(step.end_location.lat, step.end_location.lng)
              : null,
            instructions: step.instructions || '',
            path: step.path ? step.path.map(point =>
              new window.google.maps.LatLng(point.lat, point.lng)
            ) : [],
            start_location: step.start_location
              ? new window.google.maps.LatLng(step.start_location.lat, step.start_location.lng)
              : null,
            travel_mode: step.travel_mode || window.google.maps.TravelMode.DRIVING
          }))
        })),

        overview_path: route.overview_path ? route.overview_path.map(point =>
          new window.google.maps.LatLng(point.lat, point.lng)
        ) : [],

        overview_polyline: route.overview_polyline || null
      }))
    };
  } catch (error) {
    console.error('Error rehydrating directions:', error);
    return null;
  }
};

export const DirectionStateManager = {
  // Initialize direction state from API on page load
  async initializeDirectionState(deviceIds) {
    if (!Array.isArray(deviceIds) || deviceIds.length === 0) {
      console.warn("[Direction Manager] Invalid or empty deviceIds array");
      return {};
    }


    const directionPromises = deviceIds.map(async (deviceId) => {
      if (!deviceId) {
        console.warn(`[Direction Manager] Skipping invalid deviceId: ${deviceId}`);
        return { deviceId: null, direction: "unknown", source: "invalid" };
      }

      try {
        // Try Redis first (timeout for reliability)
        const apiDirection = await getVehicleDirection(deviceId);

        if (apiDirection && apiDirection !== "unknown") {
          // Validate direction
          if (apiDirection === "source_to_dest" || apiDirection === "dest_to_source") {
            const cachedState = DirectionStateCache.loadDirectionState(deviceId);
            DirectionStateCache.saveDirectionState(
              deviceId,
              apiDirection,
              cachedState?.positions || [],
              cachedState?.lastMovedPosition || null
            );
            return { deviceId, direction: apiDirection, source: "api" };
          }
        }

        // Fallback to localStorage
        const cachedState = DirectionStateCache.loadDirectionState(deviceId);
        if (cachedState?.direction && cachedState.direction !== "unknown") {
          // Validate cached direction and check freshness (e.g., <24 hours)
          if (
            (cachedState.direction === "source_to_dest" || cachedState.direction === "dest_to_source") &&
            Date.now() - cachedState.timestamp < 24 * 60 * 60 * 1000
          ) {
            console.log(`[Direction Manager] Using localStorage for ${deviceId}: ${cachedState.direction}`);
            // Retry sync to Redis up to 2 times
            for (let attempt = 1; attempt <= 2; attempt++) {
              try {
                await updateVehicleDirection(deviceId, cachedState.direction);
                console.log(`[Direction Manager] Synced localStorage to API for ${deviceId}`);
                break;
              } catch (syncError) {
                console.warn(`[Direction Manager] Sync attempt ${attempt} failed for ${deviceId}:`, syncError.message);
                if (attempt === 2) throw syncError;
                await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Backoff
              }
            }
            return { deviceId, direction: cachedState.direction, source: "localStorage" };
          }
        }

        // No direction found
        console.log(`[Direction Manager] No direction for ${deviceId}, will calculate on update`);
        return { deviceId, direction: "unknown", source: "none" };
      } catch (error) {
        console.error(`[Direction Manager] Error initializing ${deviceId}:`, error.message);
        // Fallback to localStorage
        const cachedState = DirectionStateCache.loadDirectionState(deviceId);
        if (
          cachedState?.direction &&
          (cachedState.direction === "source_to_dest" || cachedState.direction === "dest_to_source") &&
          Date.now() - cachedState.timestamp < 24 * 60 * 60 * 1000
        ) {
          return { deviceId, direction: cachedState.direction, source: "fallback" };
        }
        return { deviceId, direction: "unknown", source: "fallback" };
      }
    });

    const results = await Promise.allSettled(directionPromises);
    const initialDirections = {};

    results.forEach((result, index) => {
      const deviceId = deviceIds[index];
      if (result.status === "fulfilled" && result.value.deviceId) {
        const { deviceId: resultDeviceId, direction, source } = result.value;
        initialDirections[resultDeviceId] = direction;
        console.log(`[Direction Manager] Initialized ${resultDeviceId}: ${direction} (from ${source})`);
      } else {
        initialDirections[deviceId] = "unknown";
        console.error(`[Direction Manager] Failed to initialize ${deviceId}:`, result.reason?.message || result.reason);
      }
    });

    return initialDirections;
  },

  // Enhanced direction change handler with debouncing and race condition protection
  async handleDirectionChange(deviceId, newDirection) {
    if (!deviceId || !newDirection) {
      console.warn(`[Direction Manager] Invalid parameters: deviceId=${deviceId}, direction=${newDirection}`);
      return false;
    }


    // Clear existing timer if any
    if (apiUpdateTimers.has(deviceId)) {
      clearTimeout(apiUpdateTimers.get(deviceId));
    }

    // Check if there's already a pending update for this device
    const pendingUpdate = pendingApiUpdates.get(deviceId);
    if (pendingUpdate && pendingUpdate.direction === newDirection) {
      return true;
    }

    // Update localStorage immediately for backup
    const cachedState = DirectionStateCache.loadDirectionState(deviceId);
    DirectionStateCache.saveDirectionState(
      deviceId,
      newDirection,
      cachedState?.positions || [],
      cachedState?.lastMovedPosition || null
    );

    // Debounce API update
    return new Promise((resolve) => {
      const timer = setTimeout(async () => {
        try {
          // Mark as pending
          pendingApiUpdates.set(deviceId, { direction: newDirection, timestamp: Date.now() });

          // Update API
          await updateVehicleDirection(deviceId, newDirection);

          // Clear pending status
          pendingApiUpdates.delete(deviceId);
          apiUpdateTimers.delete(deviceId);

          resolve(true);
        } catch (error) {
          console.error(`[Direction Manager] Failed to update API direction for ${deviceId}:`, error.message);

          // Clear pending status even on failure
          pendingApiUpdates.delete(deviceId);
          apiUpdateTimers.delete(deviceId);

          // localStorage is already updated, so this isn't a complete failure
          resolve(false);
        }
      }, API_UPDATE_DEBOUNCE);

      apiUpdateTimers.set(deviceId, timer);
    });
  },

  // Cleanup method for pending operations
  cleanup() {
    // Clear all pending timers
    apiUpdateTimers.forEach((timer) => clearTimeout(timer));
    apiUpdateTimers.clear();
    pendingApiUpdates.clear();
  },

  // Get pending update status
  getPendingUpdates() {
    return Array.from(pendingApiUpdates.entries());
  }
};

// Enhanced direction updater with better error handling
export const upsertDirectionForVehicleWithAPI = async ({
  deviceId,
  currentPosition,
  stops,
  routes,
  vehicles,
  setVehicleDirections,
  onDirectionChange,
  isInitializing = false
}) => {
  if (!deviceId || !currentPosition) {
    console.warn(`[Direction] Missing required parameters: deviceId=${deviceId}, position=${!!currentPosition}`);
    return "unknown";
  }

  // Validate position data
  if (typeof currentPosition.latitude !== 'number' || typeof currentPosition.longitude !== 'number') {
    console.warn(`[Direction] Invalid position data for ${deviceId}:`, currentPosition);
    return "unknown";
  }

  try {
    // Skip calculation during initialization if we already have a direction from API
    if (isInitializing) {
      const cachedState = DirectionStateCache.loadDirectionState(deviceId);
      if (cachedState?.direction && cachedState.direction !== "unknown") {
        if (typeof setVehicleDirections === "function") {
          setVehicleDirections(prev => ({ ...prev, [deviceId]: cachedState.direction }));
        }
        return cachedState.direction;
      }
    }

    // Get current direction using existing logic
    let direction = determineVehicleDirection(deviceId, currentPosition, stops, routes, vehicles);

    // Initialize counter if not exists
    if (!recheckCounterRef[deviceId]) {
      recheckCounterRef[deviceId] = 0;
    }

    // Increment and check for periodic revalidation
    recheckCounterRef[deviceId]++;

    if (recheckCounterRef[deviceId] % RECHECK_EVERY_UPDATES === 0) {

      // Force a fresh calculation by clearing some history but keeping direction context
      const cachedState = DirectionStateCache.loadDirectionState(deviceId);
      if (cachedState && cachedState.positions && cachedState.positions.length > MIN_POINTS_FOR_DIRECTION) {
        // Keep recent positions but force re-evaluation
        const recentPositions = cachedState.positions.slice(-MIN_POINTS_FOR_DIRECTION);
        DirectionStateCache.saveDirectionState(
          deviceId,
          cachedState.direction,
          recentPositions,
          cachedState.lastMovedPosition
        );
      }

      const revalidatedDirection = determineVehicleDirection(deviceId, currentPosition, stops, routes, vehicles);
      if (revalidatedDirection !== "unknown" && revalidatedDirection !== direction) {
        direction = revalidatedDirection;
      }
    }

    // Update UI state
    if (typeof setVehicleDirections === "function") {
      setVehicleDirections(prev => ({ ...prev, [deviceId]: direction }));
    }

    // Handle direction change callbacks with API sync
    const prevDirection = prevDirectionsRef[deviceId];
    if (direction !== "unknown" && direction !== prevDirection) {

      try {
        // Update API and localStorage (with debouncing)
        await DirectionStateManager.handleDirectionChange(deviceId, direction);

        // Call the original callback if provided
        if (typeof onDirectionChange === "function") {
          await onDirectionChange(deviceId, direction);
        }

        prevDirectionsRef[deviceId] = direction;
      } catch (err) {
        console.error(`[Direction] Failed to execute direction change for ${deviceId}:`, err.message);
      }
    }

    return direction;

  } catch (error) {
    console.error(`[Direction] Unexpected error updating direction for ${deviceId}:`, error.message);
    return "unknown";
  }
};

// Enhanced cleanup function for component unmount
export const cleanupDirectionManagement = () => {

  // Clear all timers and pending operations
  DirectionStateManager.cleanup();

  // Clear internal references
  Object.keys(prevDirectionsRef).forEach(key => delete prevDirectionsRef[key]);
  Object.keys(recheckCounterRef).forEach(key => delete recheckCounterRef[key]);

};

// Utility function to get initialization status
export const getDirectionSystemStatus = () => {
  return {
    trackedVehicles: Object.keys(prevDirectionsRef),
    pendingUpdates: DirectionStateManager.getPendingUpdates(),
    recheckCounters: { ...recheckCounterRef }
  };
};
// Enhanced cache management
export const DirectionsCache = {
  // Set cache with metadata
  set(cacheKey, directionsResult, routeColor) {
    try {
      const serializedData = serializeDirections(directionsResult);
      if (serializedData) {
        const cacheObject = {
          data: serializedData,
          routeColor: routeColor,
          timestamp: Date.now(),
          version: '1.0' // For future cache format changes
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheObject));
        return true;
      }
    } catch (error) {
      console.error(`[Directions Cache] Failed to save: ${cacheKey}`, error);
      // Handle quota exceeded error
      if (error.name === 'QuotaExceededError') {
        this.clearOldCache();
      }
    }
    return false;
  },

  // Get cache with validation
  get(cacheKey, maxAge = 24 * 60 * 60 * 1000) { // Default 24 hours
    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const cacheObject = JSON.parse(cached);

      // Check cache freshness
      if (Date.now() - cacheObject.timestamp > maxAge) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      // Rehydrate the directions data
      const rehydratedData = rehydrateDirections(cacheObject.data);
      if (rehydratedData) {
        return {
          ...rehydratedData,
          routeColor: cacheObject.routeColor
        };
      }
    } catch (error) {
      console.error(`[Directions Cache] Failed to retrieve: ${cacheKey}`, error);
      localStorage.removeItem(cacheKey);
    }
    return null;
  },

  // Clear old cache entries to manage storage
  clearOldCache() {
    try {
      const keys = Object.keys(localStorage);
      const directionKeys = keys.filter(key => key.startsWith('directions_'));

      // Sort by timestamp and remove oldest entries
      const cacheEntries = directionKeys.map(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          return { key, timestamp: data.timestamp || 0 };
        } catch {
          return { key, timestamp: 0 };
        }
      }).sort((a, b) => a.timestamp - b.timestamp);

      // Remove oldest 25% of entries
      const entriesToRemove = Math.ceil(cacheEntries.length * 0.25);
      for (let i = 0; i < entriesToRemove; i++) {
        localStorage.removeItem(cacheEntries[i].key);
      }
    } catch (error) {
      console.error('[Directions Cache] Error clearing old cache:', error);
    }
  },

  // Clear all directions cache
  clearAll() {
    const keys = Object.keys(localStorage);
    const directionKeys = keys.filter(key => key.startsWith('directions_'));
    directionKeys.forEach(key => localStorage.removeItem(key));
  }
};

export const detectMovement = (newPos, lastPos) => {
  if (!lastPos || !newPos) return true;
  const distance = calculateDistance(
    {
      latitude: newPos.latitude ?? newPos.lat,
      longitude: newPos.longitude ?? newPos.lng,
    },
    {
      latitude: lastPos.latitude ?? lastPos.lat,
      longitude: lastPos.longitude ?? lastPos.lng,
    }
  );
  return distance > MOVEMENT_THRESHOLD;
};

export const getVehicleEndpoints = (deviceId, routes, vehicles) => {
  const vehicle = vehicles?.find((v) => v.device_id === deviceId);
  if (!vehicle?.routeId) return null;
  const route = routes?.find((r) => r.id === vehicle.routeId);
  if (!route?.routeCoordinates?.length) return null;
  const coords = route.routeCoordinates;
  const source = {
    latitude: parseFloat(coords[0].latitude),
    longitude: parseFloat(coords[0].longitude),
  };
  const destination = {
    latitude: parseFloat(coords[coords.length - 1].latitude),
    longitude: parseFloat(coords[coords.length - 1].longitude),
  };
  return { source, destination, vehicleRoute: route, vehicle };
};
export const DirectionStateCache = {
  // Save vehicle direction state to localStorage
  saveDirectionState(deviceId, direction, positionHistory, lastMovedPosition) {
    const storageKey = `${deviceId}_tracking`;
    const stateData = {
      direction,
      positions: positionHistory,
      lastMovedPosition,
      timestamp: Date.now(),
      deviceId,
      version: '2.0' // Version tracking for cache format
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(stateData));
      return true;
    } catch (error) {
      console.error(`[Direction Cache] Failed to save state for ${deviceId}:`, error);
      return false;
    }
  },

  // Load vehicle direction state from localStorage
  loadDirectionState(deviceId) {
    const storageKey = `${deviceId}_tracking`;
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      const data = JSON.parse(stored);

      // Check if data is too old (more than 1 hour)
      if (Date.now() - data.timestamp > 60 * 60 * 1000) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return data;
    } catch (error) {
      console.error(`[Direction Cache] Failed to load state for ${deviceId}:`, error);
      localStorage.removeItem(storageKey);
      return null;
    }
  },

  // Clear direction state for a specific device
  clearDirectionState(deviceId) {
    const storageKey = `${deviceId}_tracking`;
    localStorage.removeItem(storageKey);
  }
};

// ====== IMPROVED DIRECTION DETECTION ======
export const determineVehicleDirection = (
  deviceId,
  currentPosition,
  stops,
  routes,
  vehicles
) => {
  const normalizedPosition = currentPosition
    ? {
        latitude: parseFloat(currentPosition.latitude ?? currentPosition.lat),
        longitude: parseFloat(currentPosition.longitude ?? currentPosition.lng),
      }
    : null;
  const endpoints = getVehicleEndpoints(deviceId, routes, vehicles);

  // Load existing state from cache
  const cachedState = DirectionStateCache.loadDirectionState(deviceId);

  let positionHistory = cachedState?.positions || [];
  let lastMovedPosition = cachedState?.lastMovedPosition || null;
  let pathDirection = cachedState?.direction || "unknown";

  if (
    !normalizedPosition ||
    Number.isNaN(normalizedPosition.latitude) ||
    Number.isNaN(normalizedPosition.longitude)
  ) {
    DirectionStateCache.saveDirectionState(
      deviceId,
      pathDirection,
      positionHistory,
      lastMovedPosition
    );
    return pathDirection;
  }

  if (!endpoints) {
    // Save current state even if no endpoints
    DirectionStateCache.saveDirectionState(
      deviceId,
      pathDirection,
      positionHistory,
      lastMovedPosition
    );
    return pathDirection;
  }

  const { source, destination, vehicleRoute } = endpoints;

  // Check if vehicle is on route
  const onRoute = vehicleRoute.routeCoordinates?.some(coord => {
    const d = calculateDistance(
      { latitude: parseFloat(coord.latitude), longitude: parseFloat(coord.longitude) },
      normalizedPosition
    );
    return d <= ROUTE_PROXIMITY_THRESHOLD;
  });

  if (!onRoute) {
    DirectionStateCache.saveDirectionState(
      deviceId,
      pathDirection,
      positionHistory,
      lastMovedPosition
    );
    return pathDirection;
  }

  // Check for movement
  const moved = detectMovement(normalizedPosition, lastMovedPosition);
  if (moved) {
    positionHistory.push({
      ...normalizedPosition,
      timestamp: Date.now()
    });
    lastMovedPosition = normalizedPosition;

    // Keep only recent history
    if (positionHistory.length > BACKUP_POINTS_FOR_RECHECK) {
      positionHistory = positionHistory.slice(-BACKUP_POINTS_FOR_RECHECK);
    }

  }

  // Distance calculations
  const distToSource = calculateDistance(normalizedPosition, source);
  const distToDest = calculateDistance(normalizedPosition, destination);
  const nearSource = distToSource <= TERMINAL_THRESHOLD;
  const nearDest = distToDest <= TERMINAL_THRESHOLD;

  let newDirection = pathDirection;

  // Terminal detection logic - force direction change if at terminals
  if (nearSource && pathDirection !== "source_to_dest") {
    newDirection = "source_to_dest";
    positionHistory = [{ ...normalizedPosition, timestamp: Date.now() }];
  } else if (nearDest && pathDirection !== "dest_to_source") {
    newDirection = "dest_to_source";
    positionHistory = [{ ...normalizedPosition, timestamp: Date.now() }];
  } else if (positionHistory.length >= MIN_POINTS_FOR_DIRECTION) {
    // Trend-based analysis with improved logic
    const recentPoints = positionHistory.slice(-MIN_POINTS_FOR_DIRECTION);

    let sourceTrend = 0;
    let destTrend = 0;
    let validComparisons = 0;

    for (let i = 1; i < recentPoints.length; i++) {
      const prev = recentPoints[i - 1];
      const curr = recentPoints[i];

      const prevToSource = calculateDistance(prev, source);
      const currToSource = calculateDistance(curr, source);
      const prevToDest = calculateDistance(prev, destination);
      const currToDest = calculateDistance(curr, destination);

      // Only count significant movements
      const movementDistance = calculateDistance(prev, curr);
      if (movementDistance > MOVEMENT_THRESHOLD / 2) {
        sourceTrend += (currToSource - prevToSource);
        destTrend += (currToDest - prevToDest);
        validComparisons++;
      }
    }

    if (validComparisons >= 3) { // Need at least 3 valid movements
      const avgSourceTrend = sourceTrend / validComparisons;
      const avgDestTrend = destTrend / validComparisons;

      // More decisive thresholds
      const TREND_THRESHOLD = 5; // meters average movement toward/away

      if (avgSourceTrend > TREND_THRESHOLD && avgDestTrend < -TREND_THRESHOLD) {
        newDirection = "source_to_dest";
      } else if (avgSourceTrend < -TREND_THRESHOLD && avgDestTrend > TREND_THRESHOLD) {
        newDirection = "dest_to_source";
      }
    }
  }

  // Save updated state to localStorage
  DirectionStateCache.saveDirectionState(
    deviceId,
    newDirection,
    positionHistory,
    lastMovedPosition
  );

  if (newDirection !== pathDirection) {
  }

  return newDirection;
};

// ====== IMPROVED DIRECTION UPDATER ======
export const upsertDirectionForVehicle = async ({
  deviceId,
  currentPosition,
  stops,
  routes,
  vehicles,
  setVehicleDirections,
  onDirectionChange,
}) => {
  if (!deviceId || !currentPosition) {
    console.warn(`[Direction] Missing deviceId or position for vehicle update`);
    return;
  }

  // Get current direction
  let direction = determineVehicleDirection(deviceId, currentPosition, stops, routes, vehicles);

  // Initialize counter if not exists
  if (!recheckCounterRef[deviceId]) {
    recheckCounterRef[deviceId] = 0;
  }

  // Increment and check for periodic revalidation
  recheckCounterRef[deviceId]++;

  if (recheckCounterRef[deviceId] % RECHECK_EVERY_UPDATES === 0) {

    // Force a fresh calculation by clearing some history but keeping direction context
    const cachedState = DirectionStateCache.loadDirectionState(deviceId);
    if (cachedState && cachedState.positions.length > MIN_POINTS_FOR_DIRECTION) {
      // Keep recent positions but force re-evaluation
      const recentPositions = cachedState.positions.slice(-MIN_POINTS_FOR_DIRECTION);
      DirectionStateCache.saveDirectionState(
        deviceId,
        cachedState.direction,
        recentPositions,
        cachedState.lastMovedPosition
      );
    }

    const revalidatedDirection = determineVehicleDirection(deviceId, currentPosition, stops, routes, vehicles);
    if (revalidatedDirection !== "unknown" && revalidatedDirection !== direction) {
      direction = revalidatedDirection;
    }
  }

  // Update UI state
  if (typeof setVehicleDirections === "function") {
    setVehicleDirections(prev => ({ ...prev, [deviceId]: direction }));
  }

  // Handle direction change callbacks
  const prevDirection = prevDirectionsRef[deviceId];
  if (direction !== "unknown" && direction !== prevDirection) {

    try {
      if (typeof onDirectionChange === "function") {
        await onDirectionChange(deviceId, direction);
      }
      prevDirectionsRef[deviceId] = direction;
    } catch (err) {
      console.error(`[Direction] Failed to execute onDirectionChange for ${deviceId}:`, err);
    }
  }

  return direction;
};

export const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  return hash;
};

// Helper to extract path points as plain objects for rendering polylines if needed
export const extractPathPoints = (directions) => {
  if (!directions?.routes?.[0]?.legs) return [];
  const points = [];
  directions.routes[0].legs.forEach((leg) => {
    leg.steps.forEach((step) => {
      step.path.forEach((point) => {
        points.push({
          lat: typeof point.lat === "function" ? point.lat() : point.lat,
          lng: typeof point.lng === "function" ? point.lng() : point.lng,
        });
      });
    });
  });
  return points;
};

